import { Clipboard } from '@angular/cdk/clipboard'
import { AfterViewInit, Component, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, OnInit } from '@angular/core'
import { Subject } from 'rxjs'
import { filter, debounceTime } from 'rxjs/operators'
import { MatDialog } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { environment } from '../../../../environments/environment'
import { 
    ExpireWishlistCommand, 
    ExpireWishlistGQL, 
    Wishlist, 
    WishlistsGQL,   
    PartnerUpdateTravelAgentListStaffGQL,
    TravelAgent
 } from '../../../../graphql'
import { dialogWidth } from '../../../constant'
import { Amplitude } from '../../../injectable'
import { UnsubscribeOnDestroy } from '../../../utility'
import { CreateWishlistComponent } from './create-wishlist/create-wishlist'
import { CurrencyService } from '../../../injectable/currency-service'
import { Router } from '@angular/router'
import { TravelAgentService } from '../../../injectable/travel-agent-service'
import { last, concatMap, take } from 'rxjs/operators'


@Component({
    selector: 'app-wishlists',
    styleUrls: ['wishlists.scss'],
    templateUrl: 'wishlists.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WishlistsComponent extends UnsubscribeOnDestroy implements AfterViewInit, OnInit {
    wishlists: Array<Wishlist>
    loading = true;
    dataSource: MatTableDataSource<Wishlist>
    columnsToDisplay = ['name', 'status', 'wishes', 'actions'];

    travelAgents: [TravelAgent]
    companyName: string
    companyLogo: string | null | undefined
    currency: string
    shareUrl: string

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    private readonly resizeSubject = new Subject<void>();

    trackByWishlist(index: number, wishlist: Wishlist): string {
        return wishlist?.wishlistId;
    }

    constructor(
        private _amplitude: Amplitude,
        private _clipboard: Clipboard,
        private _dialog: MatDialog,
        private _snackBar: MatSnackBar,
        private _expireWishlistGQL: ExpireWishlistGQL,
        private _wishlistsGQL: WishlistsGQL,
        private _listStaffGQL: PartnerUpdateTravelAgentListStaffGQL,
        private _currencyService: CurrencyService,
        private _router: Router,
        private _travelAgentService: TravelAgentService,
        private _cdr: ChangeDetectorRef
    ) {
        super()

        this.currency = this._currencyService.getPresentmentCurrency()

        this._currencyService.presentmentCurrency$.subscribe(currency => {
            this.currency = currency
        })
    }

    ngAfterViewInit() {
        // TODO: Not working for some reason, while hotels.ts does the seemingly exact same thing and it works.
        //       PS. I've now changed to use isEmpty and a template in the HTML file - that definitely breaks the line below.
        // this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
        // this.shareUrl = `${this.shareLink(wishlist)}?currency=${this.currency}&company_name=${this.companyName}${this.companyLogo ? '&company_logo=' + this.companyLogo : ''}`, '_blank'
        this.refreshWishlists()
        this.retrieveTravelAgentId() 
    }

    get isEmpty(): boolean {
        return !this.loading && (!this.dataSource?.data?.length || this.dataSource.data.length === 0);
    }

    navigateTo($event: MouseEvent, wishlist: Wishlist) {
        window.open(this.shareLink(wishlist), '_blank')

        $event.stopPropagation()
        $event.preventDefault()
        return false
    }

    editWishlist(wishlist: Wishlist) {
        this._router.navigateByUrl(`/wishlists/${wishlist.wishlistId}${this.getQueryParams()}`)
    }

    copyLink($event: MouseEvent, wishlist: Wishlist) {
        this._clipboard.copy(this.shareLink(wishlist))
        this._snackBar.open('Share link copied to your clipboard')

        $event.stopPropagation()
        $event.preventDefault()
        return false
    }

    createWishlist($event: MouseEvent) {
        this._subscriptions.push(this._dialog.open(CreateWishlistComponent, { width: dialogWidth.xs })
            .afterClosed().subscribe((_: string) => {
                setTimeout(() => this.refreshWishlists(), 1000)
            }))

        $event.stopPropagation()
        $event.preventDefault()
        return false
    }

    archiveWishlist($event: Event, wishlist: Wishlist) {
        $event.stopPropagation()

        const command: ExpireWishlistCommand = {
            wishlistId: wishlist.wishlistId,
        }
        this._subscriptions.push(this._expireWishlistGQL.mutate({ input: command }).subscribe(({ data }) => {
            this._snackBar.open('Proposal removed - ' + wishlist.name)
            setTimeout(() => this.refreshWishlists(), 700)
        }, (error) => {
            this._snackBar.open('There was an error updating your wishlist, please try again later')
            console.error('Error updating wishlist', error)
        }))
    }

    ngOnInit() {
        // Setup debounced resize handler
        this.resizeSubject.pipe(
            debounceTime(150)
        ).subscribe(() => {
            window.dispatchEvent(new Event('resize'))
        })
    }

    refreshWishlists() {
        this.loading = true
        this._cdr.markForCheck()
        
        this._amplitude.logEvent('Wishlists Page Viewed')

        this._subscriptions.push(
            this._wishlistsGQL.watch({}, {fetchPolicy: 'no-cache'})
                .valueChanges
                .pipe(
                    filter(result => !result.loading),
                    take(1)
                )
                .subscribe({
                    next: (result) => {
                        if (result.errors?.length) {
                            throw new Error(`Failed to fetch wishlists for listing: ${result.errors[0].message}`)
                        }
                        
                        this.wishlists = result.data.wishlists as Array<Wishlist>
                        this.dataSource = new MatTableDataSource(this.wishlists)
                        this.dataSource.paginator = this.paginator
                        this.dataSource.sort = this.sort

                        this._amplitude.logEvent('Wishlists Page Viewed')
                        this._amplitude.set('wishlistCount', this.wishlists?.length)
                        
                        this.loading = false
                        this._cdr.markForCheck()
                        
                        // Trigger debounced resize
                        this.resizeSubject.next()
                    },
                    error: (error) => {
                        console.error('Error fetching wishlists:', error)
                        this.loading = false
                        this._cdr.markForCheck()
                    }
                })
        )
    }

    getQueryParams() {
        return `?currency=${this.currency}&company_name=${encodeURIComponent(this.companyName)}${this.companyLogo ? '&company_logo=' + encodeURIComponent(this.companyLogo) : ''}`
    }

    shareLink(wishlist: Wishlist): string {
        return `${environment.memberUrl}public-wishlist/${wishlist.wishlistId}${this.getQueryParams()}`
    }

    emailLink($event: Event, wishlist: Wishlist) {
        $event.stopPropagation()
        location.href = `mailto:?subject=${wishlist.name}&body=${encodeURIComponent(this.shareLink(wishlist))}`
    }

    getUniqueTravelDates(wishes: any[]): Array<{checkin: Date, checkout: Date}> {
        const uniqueDates = new Map<string, {checkin: Date, checkout: Date}>()

        wishes?.forEach(wish => {
            if (wish.travelDates) {
                const key = `${wish.travelDates.checkin}_${wish.travelDates.checkout}`
                if (!uniqueDates.has(key)) {
                    uniqueDates.set(key, {
                        checkin: new Date(wish.travelDates.checkin),
                        checkout: new Date(wish.travelDates.checkout)
                    })
                }
            }
        })

        return Array.from(uniqueDates.values())
    }

    private retrieveTravelAgentId() {
        this._travelAgentService
        .refreshTravelAgent()
        .pipe(
            last(),
            concatMap(() => this._travelAgentService.travelAgentSubject),
            take(1),
        )
        .subscribe(travelAgent => {
            if (travelAgent?.travelAgentId) {
                this.companyName = travelAgent?.name
                this.retrieveTravelAgentStaff(travelAgent?.travelAgentId)
            }
        })
    }

    private retrieveTravelAgentStaff(travelAgentId: string) {
        this._subscriptions.push(this._listStaffGQL
            .fetch({ travelAgentId: travelAgentId}, { fetchPolicy: 'no-cache' })
            .subscribe(result => {
                if (!result.loading) {
                    const staff = result.data.travelAgentStaff
                    this.companyLogo = staff?.photoUrl
                }
            }))
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.resizeSubject.complete();
    }
}
